import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { UserPlus, Save, ArrowLeft, Upload, User, Loader2, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { db, auth, storage } from "../../firebase/config";
import {
  doc,
  getDocs,
  setDoc,
  query,
  collection,
  where,
} from "firebase/firestore";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import ContentLoader from "../../authentication/ContentLoader";
import Header from "../Header";
import AdminSidebar from "../Sidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import imageCompression from 'browser-image-compression';
import { Camera } from "lucide-react";

// Declare process.env types
declare global {
  namespace NodeJS {
    interface ProcessEnv {
      NODE_ENV: "development" | "production" | "test";
    }
  }
}

interface TeacherRegistrationFormProps {
  onBack: () => void;
}

interface Subject {
  subjectId: string;
  name: string;
  code: string;
  department: string;
  type: "Core" | "Elective";
  assignedTeachers: number;
  teacherNames?: string[];
  description?: string;
  headOfDepartment?: string;
  schoolID: string;
  submittedBy: string;
  timestamp: string;
}

interface Teacher {
  id: string;
  employeeId: string;
  title?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  phone: string;
  secondaryPhone?: string;
  address?: string;
  dateOfBirth: string;
  nationalId: string;
  position: string[];
  tscNumber?: string;
  kraPin?: string;
  profilePicture: string | null;
  documents: string[];
  subjectsOfExpertiseId: string[];
  otherDocuments?: string;
  submittedBy: string;
  timestamp: string;
  emergencyContact?: string;
}

interface FormData {
  profilePicture: File | null;
  employeeId: string;
  title?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  phone: string;
  secondaryPhone?: string;
  physicalAddress?: string;
  dateOfBirth: string;
  nationalId: string;
  position: string[];
  tscNumber?: string;
  kraPin?: string;
  subjectsOfExpertiseId: string[];
  otherDocuments?: string;
  documents: File[];
  emergencyContact?: string;
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface CustomClaims {
  affiliations?: Affiliation[];
  defaultAffiliation?: Affiliation;
  schoolID?: string;
}

const TeacherRegistrationForm = ({ onBack }: TeacherRegistrationFormProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const profilePictureInputRef = useRef<HTMLInputElement>(null);
  const documentsInputRef = useRef<HTMLInputElement>(null);

  const [submittedBy, setSubmittedBy] = useState("");
  const [schoolID, setSchoolID] = useState("");
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [subjects, setSubjects] = useState<Subject[]>([]);

  const [formData, setFormData] = useState<FormData>({
    profilePicture: null,
    employeeId: "",
    title: "",
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    phone: "",
    secondaryPhone: "",
    physicalAddress: "",
    dateOfBirth: "",
    nationalId: "",

    position: [],
    tscNumber: "",
    kraPin: "",
    subjectsOfExpertiseId: [],
    otherDocuments: "",
    documents: [],
    emergencyContact: "",
  });

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const VALID_IMAGE_TYPES = ["image/jpeg", "image/png"];
  const VALID_DOC_TYPES = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "image/jpeg",
    "image/png",
  ];

  /* ───── Fetch Subjects ───── */
  useEffect(() => {
    if (!schoolID || !hasValidClaims) return;

    const fetchSubjects = async () => {
      try {
        const subjectsCollection = collection(db, `schools/${schoolID}/subjects`);
        const subjectsSnapshot = await getDocs(subjectsCollection);
        const subjectsData = subjectsSnapshot.docs
          .map((doc) => ({
            ...(doc.data() as Subject),
            subjectId: doc.id,
          }))
          .filter((subject) => subject.name && subject.name.trim() !== "");

        if (process.env.NODE_ENV !== "production") {
          console.log("Fetched subjects:", subjectsData);
        }

        setSubjects(subjectsData);
      } catch (error) {
        console.error("Error fetching subjects:", error);
        toast({
          title: "Data Fetch Failed",
          description: "Unable to load subjects.",
          variant: "destructive",
          duration: 5000,
        });
      }
    };

    fetchSubjects();
  }, [schoolID, hasValidClaims, toast]);

  /* ───── Auth & Claims Verification ───── */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please log in to register a teacher.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
        setIsAuthLoading(false);
        return;
      }

      setSubmittedBy(user.uid);
      try {
        const token = await getIdTokenResult(user, true);
        const claims = token.claims as CustomClaims;
        const affiliations: Affiliation[] = claims.affiliations || [];
        const defaultAff: Affiliation | undefined = claims.defaultAffiliation;
        const claimSchoolID: string | undefined = claims.schoolID;

        if (process.env.NODE_ENV !== "production") {
          console.log("Claims:", { affiliations, defaultAff, claimSchoolID });
        }

        const adminAff = defaultAff?.role === "admin" && defaultAff.schoolID
          ? defaultAff
          : affiliations.find(aff => aff.role === "admin" && aff.schoolID);

        if (adminAff && adminAff.schoolID === claimSchoolID) {
          if (adminAff.schoolID) {
            setSchoolID(adminAff.schoolID);
          }
          setHasValidClaims(true);
        } else {
          if (process.env.NODE_ENV !== "production") console.log("Retrying claims after 10s...");
          await new Promise(resolve => setTimeout(resolve, 10000));
          const retryToken = await getIdTokenResult(user, true);
          const retryClaims = retryToken.claims as CustomClaims;
          const retryAffiliations: Affiliation[] = retryClaims.affiliations || [];
          const retryDefaultAff: Affiliation | undefined = retryClaims.defaultAffiliation;
          const retrySchoolID: string | undefined = retryClaims.schoolID;

          const retryAdminAff = retryDefaultAff?.role === "admin" && retryDefaultAff.schoolID
            ? retryDefaultAff
            : retryAffiliations.find(aff => aff.role === "admin" && aff.schoolID);

          if (retryAdminAff && retryAdminAff.schoolID === retrySchoolID) {
            setSchoolID(retryAdminAff.schoolID || "");
            setHasValidClaims(true);
          } else {
            throw new Error("Invalid admin claims");
          }
        }
      } catch (error: any) {
        console.error("Claims validation error:", error);
        toast({
          title: "Missing Permissions",
          description: "You lack permissions to register teachers.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
      }
      setIsAuthLoading(false);
    });

    return () => {
      if (process.env.NODE_ENV !== "production") console.log("Unsubscribing auth");
      unsubscribe();
    };
  }, [toast, navigate]);

  /* ───── Handlers ───── */
  const handleInputChange = (field: keyof Omit<FormData, 'profilePicture' | 'subjectsOfExpertiseId' | 'documents' | 'position'>, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddPosition = (value: string) => {
    if (value && !formData.position.includes(value)) {
      setFormData(prev => ({
        ...prev,
        position: [...prev.position, value]
      }));
    }
  };

  const handleRemovePosition = (positionToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      position: prev.position.filter(p => p !== positionToRemove)
    }));
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate type first
    if (!VALID_IMAGE_TYPES.includes(file.type)) {
      toast({
        title: "Invalid File Type",
        description: "Profile picture must be JPEG or PNG.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Processing Image",
      description: "Optimizing photo for storage...",
      duration: 2000,
    });

    const options = {
      maxSizeMB: 0.1, // ~100KB
      maxWidthOrHeight: 1280,
      useWebWorker: true,
      fileType: 'image/jpeg' as string,
    };

    try {
      const compressedFile = await imageCompression(file, options);
      
      const fileWithNewName = new File([compressedFile], file.name, {
        type: compressedFile.type,
      });
      
      setFormData(prev => ({ ...prev, profilePicture: fileWithNewName }));
      
      toast({
        title: "Photo Ready",
        description: `Reduced from ${(file.size / 1024 / 1024).toFixed(1)}MB to ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`,
        duration: 3000,
      });
    } catch (error) {
      console.error("Image compression error:", error);
      setFormData(prev => ({ ...prev, profilePicture: file })); // Fallback
      toast({
        title: "Optimization Failed",
        description: "Photo was uploaded without compression.",
        variant: "destructive",
      });
    }
  };

  const triggerCamera = () => {
    if (profilePictureInputRef.current) {
      profilePictureInputRef.current.click();
    }
  };



  const handleDocumentsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const validFiles = Array.from(files).filter(file => VALID_DOC_TYPES.includes(file.type) && file.size <= MAX_FILE_SIZE);
      if (validFiles.length !== files.length) {
        toast({
          title: "Invalid Files",
          description: "Some files were rejected. Ensure documents are PDF, DOC, DOCX, JPG, or PNG and less than 5MB.",
          variant: "destructive",
          duration: 5000,
        });
        setErrorMessage("Some files were rejected. Ensure documents are PDF, DOC, DOCX, JPG, or PNG and less than 5MB.");
        setIsErrorDialogOpen(true);
        return;
      }
      setFormData(prev => ({ ...prev, documents: validFiles }));
    }
  };

  const handleSubjectChange = (subjectId: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      subjectsOfExpertiseId: checked
        ? [...prev.subjectsOfExpertiseId, subjectId]
        : prev.subjectsOfExpertiseId.filter(s => s !== subjectId),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!schoolID || !hasValidClaims || !auth.currentUser) {
      toast({
        title: "Authentication Error",
        description: "Missing permissions or login.",
        variant: "destructive",
        duration: 5000,
      });
      navigate("/login-page");
      return;
    }

    setIsUploading(true);
    try {
      const token = await getIdTokenResult(auth.currentUser, true);
      const claims = token.claims as CustomClaims;
      const affiliations: Affiliation[] = claims.affiliations || [];
      const defaultAff: Affiliation | undefined = claims.defaultAffiliation;
      const claimSchoolID: string | undefined = claims.schoolID;

      if (process.env.NODE_ENV !== "production") {
        console.log("Submit claims:", { affiliations, defaultAff, claimSchoolID });
      }

      const adminAff = defaultAff?.role === "admin" && defaultAff.schoolID === schoolID
        ? defaultAff
        : affiliations.find(aff => aff.role === "admin" && aff.schoolID === schoolID);

      if (!adminAff) {
        if (process.env.NODE_ENV !== "production") console.log("Retrying claims after 10s...");
        await new Promise(resolve => setTimeout(resolve, 10000));
        const retryToken = await getIdTokenResult(auth.currentUser, true);
        const retryClaims = retryToken.claims as CustomClaims;
        const retryAffiliations: Affiliation[] = retryClaims.affiliations || [];
        const retryDefaultAff: Affiliation | undefined = retryClaims.defaultAffiliation;

        if (!(retryDefaultAff?.role === "admin" && retryDefaultAff.schoolID === schoolID) &&
            !retryAffiliations.find(aff => aff.role === "admin" && aff.schoolID === schoolID)) {
          throw new Error("Invalid admin claims");
        }
      }

      if (
        !formData.firstName ||
        !formData.lastName ||
        !formData.email ||
        !formData.phone ||
        !formData.dateOfBirth ||
        !formData.nationalId ||
        !formData.nationalId ||
        formData.position.length === 0 ||
        !formData.employeeId
      ) {
        throw new Error("Please fill in all required fields.");
      }

      const teachersCollection = collection(db, `schools/${schoolID}/teachers`);
      const [employeeIdSnapshot, nationalIdSnapshot] = await Promise.all([
        getDocs(query(teachersCollection, where("employeeId", "==", formData.employeeId))),
        getDocs(query(teachersCollection, where("nationalId", "==", formData.nationalId))),
      ]);

      if (!employeeIdSnapshot.empty) throw new Error("A teacher with this Employee ID already exists.");
      if (!nationalIdSnapshot.empty) throw new Error("A teacher with this National ID already exists.");

      const newTeacherId = doc(collection(db, `schools/${schoolID}/teachers`)).id;
      let profilePictureUrl: string | null = null;
      if (formData.profilePicture) {
        const profilePictureRef = ref(storage, `schools/${schoolID}/teachers/${newTeacherId}/profilePicture`);
        await uploadBytes(profilePictureRef, formData.profilePicture);
        profilePictureUrl = await getDownloadURL(profilePictureRef);
      }

      const documentUrls = await Promise.all(
        formData.documents.map(async (file, index) => {
          let fileToUpload = file;
          // specific compression for document images
          if (file.type === "image/jpeg" || file.type === "image/png") {
             try {
               const options = {
                 maxSizeMB: 0.2, // 200KB for docs
                 maxWidthOrHeight: 1600, // readable text
                 useWebWorker: true,
                 fileType: file.type as string,
               };
               fileToUpload = await imageCompression(file, options);
             } catch (error) {
               console.warn("Doc compression failed, using original", error);
             }
          }
          
          const documentRef = ref(storage, `schools/${schoolID}/teachers/${newTeacherId}/documents/doc_${index}_${fileToUpload.name}`);
          await uploadBytes(documentRef, fileToUpload);
          return getDownloadURL(documentRef);
        })
      );

      const teacherData: Teacher = {
        id: newTeacherId,
        employeeId: formData.employeeId,
        title: formData.title,
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        secondaryPhone: formData.secondaryPhone,
        address: formData.physicalAddress,
        dateOfBirth: formData.dateOfBirth,
        nationalId: formData.nationalId,
        position: formData.position,
        tscNumber: formData.tscNumber,
        kraPin: formData.kraPin,
        profilePicture: profilePictureUrl,
        documents: documentUrls,
        subjectsOfExpertiseId: formData.subjectsOfExpertiseId,
        otherDocuments: formData.otherDocuments,
        submittedBy,
        timestamp: new Date().toISOString(),
        emergencyContact: formData.emergencyContact,
      };

      await setDoc(doc(db, `schools/${schoolID}/teachers`, newTeacherId), {
        teacherId: teacherData.id,
        employeeId: teacherData.employeeId,
        title: teacherData.title,
        firstName: teacherData.firstName,
        middleName: teacherData.middleName,
        lastName: teacherData.lastName,
        email: teacherData.email,
        phoneNumber: teacherData.phone,
        secondaryPhone: teacherData.secondaryPhone,
        address: teacherData.address,
        dateOfBirth: teacherData.dateOfBirth,
        nationalId: teacherData.nationalId,
        teacherType: teacherData.position,
        tscNumber: teacherData.tscNumber,
        kraPin: teacherData.kraPin,
        profilePictureUrl: teacherData.profilePicture,
        documentUrls: teacherData.documents,
        subjectsOfExpertiseId: teacherData.subjectsOfExpertiseId,
        otherDocuments: teacherData.otherDocuments,
        submittedBy: teacherData.submittedBy,
        timestamp: teacherData.timestamp,
        emergencyContact: teacherData.emergencyContact,
      });

      const inviteId = doc(collection(db, "invitations")).id;
      const schoolDetailsSnapshot = await getDocs(query(collection(db, `schools/${schoolID}/schoolDetails`)));
      const schoolName = schoolDetailsSnapshot.empty ? "Unknown School" : (schoolDetailsSnapshot.docs[0].data() as { name?: string }).name || "Unknown School";

      const inviteData = {
        inviteId,
        email: formData.email,
        teacherName: `${formData.firstName} ${formData.middleName ? formData.middleName + " " : ""}${formData.lastName}`,
        schoolName,
        schoolID,
        role: "school_staff",
        status: "pending",
        createdAt: new Date().toISOString(),
        invitedBy: submittedBy,
      };

      await setDoc(doc(db, "invitations", inviteId), inviteData);

      setFormData({
        profilePicture: null,
        employeeId: "",
        title: "",
        firstName: "",
        middleName: undefined,
        lastName: "",
        email: "",
        phone: "",
        secondaryPhone: undefined,
        physicalAddress: undefined,
        dateOfBirth: "",
        nationalId: "",
        position: [],
        tscNumber: undefined,
        kraPin: undefined,
        subjectsOfExpertiseId: [],
        otherDocuments: undefined,
        documents: [],
        emergencyContact: undefined,
      });
      if (profilePictureInputRef.current) profilePictureInputRef.current.value = "";
      if (documentsInputRef.current) documentsInputRef.current.value = "";

      setIsSuccessDialogOpen(true);
      toast({
        title: "Teacher Registered",
        description: `${formData.firstName} ${formData.lastName} added successfully.`,
        duration: 3000,
        className: "bg-green-500 text-white border-green-600",
      });
    } catch (error: any) {
      console.error("Registration error:", error);
      toast({
        title: error.code === "permission-denied" ? "Permission Error" : "Registration Failed",
        description: error.code === "permission-denied"
          ? "Insufficient permissions. Ensure your account is admin."
          : error.message || "Unexpected error.",
        variant: "destructive",
        duration: 5000,
      });
      setErrorMessage(error.message || "Failed to register teacher.");
      setIsErrorDialogOpen(true);
    } finally {
      setIsUploading(false);
    }
  };



  return (
    <SidebarProvider>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
        .font-roboto { font-family: 'Roboto', sans-serif; }
      `}</style>
      <div className="fixed inset-0 bg-gray-50 dark:bg-gray-900 overflow-hidden font-roboto">
        <SidebarProvider className="h-full overflow-hidden">
          <div className="flex h-full w-full">
            <AdminSidebar onSectionChange={() => {}} />
            <div className="flex flex-col h-full flex-1 min-w-0">
              <Header />
              <main className="flex-1 min-h-0 overflow-auto p-3 sm:p-6 dark:[color-scheme:dark]">
                <ContentLoader isLoading={isAuthLoading || !hasValidClaims} message="">
            <div className="mb-6">
              <Button
                variant="outline"
                onClick={onBack}
                className="m-2 h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4 text-muted-foreground" />
                Back to Teachers & Staff
              </Button>
              <h1 className="text-2xl font-bold text-black dark:text-white mb-2 flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-gray-400" />
                Register New Teacher/Staff
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Add a new teacher or staff member to the school system
              </p>
            </div>
            <Card className="w-full max-w-4xl mx-auto bg-white dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B] hover:bg-[#F8FAFC] transition-colors">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-black dark:text-white">
                  <User className="h-5 w-5 text-gray-400" />
                  Teacher/Staff Information
                </CardTitle>
                <CardDescription className="text-gray-600 dark:text-gray-400">
                  Please fill in all required information for the new teacher or staff member
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-full bg-gray-200 dark:bg-[#1E293B] flex items-center justify-center overflow-hidden relative">
                        {formData.profilePicture ? (
                          <>
                            <img
                              src={URL.createObjectURL(formData.profilePicture)}
                              alt="Profile"
                              className="w-full h-full object-cover"
                            />
                          </>
                        ) : (
                          <User className="h-8 w-8 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <Label htmlFor="profilePicture" className="text-black dark:text-white">
                          Profile Picture
                        </Label>
                        <div className="flex gap-2 mt-1">
                          <Button 
                            type="button" 
                            variant="outline" 
                            onClick={triggerCamera}
                            className="flex-1 h-10 items-center gap-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31]"
                          >
                            <Camera className="h-4 w-4" />
                            Take Photo / Choose
                          </Button>
                          {formData.profilePicture && (
                            <Button 
                              type="button" 
                              variant="ghost" 
                              onClick={() => {
                                setFormData(prev => ({ ...prev, profilePicture: null }));
                                if (profilePictureInputRef.current) profilePictureInputRef.current.value = "";
                              }}
                              className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10"
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                        <input
                          id="profilePicture"
                          type="file"
                          accept="image/*"
                          capture="environment"
                          ref={profilePictureInputRef}
                          onChange={handlePhotoChange}
                          className="hidden"
                        />
                        <p className="text-[10px] text-gray-500 mt-1">Photos are optimized to save space while maintaining quality.</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4 border-t border-[#E2E8F0] dark:border-[#1E293B] pt-4">
                    <h3 className="text-lg font-semibold text-black dark:text-white">
                      Personal Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="employeeId" className="text-black dark:text-white">
                          Employee ID *
                        </Label>
                        <Input
                          id="employeeId"
                          value={formData.employeeId}
                          onChange={(e) => handleInputChange("employeeId", e.target.value)}
                          required
                          className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                        />
                      </div>
                      <div>
                        <Label htmlFor="nationalId" className="text-black dark:text-white">
                          National ID Number *
                        </Label>
                        <Input
                          id="nationalId"
                          value={formData.nationalId}
                          onChange={(e) => handleInputChange("nationalId", e.target.value)}
                          required
                          className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <Label htmlFor="title" className="text-black dark:text-white">
                          Title
                        </Label>
                        <Select
                          value={formData.title || ""}
                          onValueChange={(value) => handleInputChange("title", value)}
                        >
                          <SelectTrigger className="h-10 rounded-md bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white">
                            <SelectValue placeholder="Select title" />
                          </SelectTrigger>
                          <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                            <SelectItem value="Mr">Mr</SelectItem>
                            <SelectItem value="Mrs">Mrs</SelectItem>
                            <SelectItem value="Miss">Miss</SelectItem>
                            <SelectItem value="Ms">Ms</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="firstName" className="text-black dark:text-white">
                          First Name *
                        </Label>
                        <Input
                          id="firstName"
                          value={formData.firstName}
                          onChange={(e) => handleInputChange("firstName", e.target.value)}
                          required
                          className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                        />
                      </div>
                      <div>
                        <Label htmlFor="middleName" className="text-black dark:text-white">
                          Middle Name <span className="text-gray-400 text-xs">(Optional)</span>
                        </Label>
                        <Input
                          id="middleName"
                          value={formData.middleName || ""}
                          onChange={(e) => handleInputChange("middleName", e.target.value)}
                          className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName" className="text-black dark:text-white">
                          Last Name *
                        </Label>
                        <Input
                          id="lastName"
                          value={formData.lastName}
                          onChange={(e) => handleInputChange("lastName", e.target.value)}
                          required
                          className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="dateOfBirth" className="text-black dark:text-white">
                          Date of Birth *
                        </Label>
                        <Input
                          id="dateOfBirth"
                          type="date"
                          value={formData.dateOfBirth}
                          onChange={(e) => handleInputChange("dateOfBirth", e.target.value)}
                          required
                          className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-black dark:text-white">
                          Position(s) *
                        </Label>
                        <div className="space-y-2">
                          <div className="flex flex-wrap gap-2 mb-2">
                            {formData.position.map((pos) => {
                              const badgeColors: Record<string, string> = {
                                teacher: "bg-[#2563EB] dark:bg-[#3B82F6] hover:bg-[#1E40AF] dark:hover:bg-[#2563EB] text-white",
                                librarian: "bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E] text-white",
                                lab_technician: "bg-cyan-600 dark:bg-cyan-500 hover:bg-cyan-700 dark:hover:bg-cyan-600 text-white",
                                bursar: "bg-indigo-600 dark:bg-indigo-500 hover:bg-indigo-700 dark:hover:bg-indigo-600 text-white",
                                head_of_department: "bg-purple-600 dark:bg-purple-500 hover:bg-purple-700 dark:hover:bg-purple-600 text-white",
                                deputy_principal: "bg-amber-600 dark:bg-amber-500 hover:bg-amber-700 dark:hover:bg-amber-600 text-white",
                                principal: "bg-red-600 dark:bg-red-500 hover:bg-red-700 dark:hover:bg-red-600 text-white",
                              };
                              const colorClass = badgeColors[pos] || "bg-gray-500 text-white";
                              return (
                                <Badge key={pos} className={`pl-2 pr-1 py-1 flex items-center gap-1 ${colorClass}`}>
                                  {pos.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                  <button
                                    type="button"
                                    onClick={() => handleRemovePosition(pos)}
                                    className="hover:bg-white/20 rounded-full p-0.5"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </Badge>
                              );
                            })}
                          </div>
                          <Select
                            onValueChange={handleAddPosition}
                          >
                            <SelectTrigger className="h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white">
                              <SelectValue placeholder="Add position..." />
                            </SelectTrigger>
                            <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white">
                              <SelectItem value="teacher">Full-Time Teacher</SelectItem>
                              <SelectItem value="contract_teacher">Contract Teacher</SelectItem>
                              <SelectItem value="teacher_on_practice">Teacher on Practice</SelectItem>
                              <SelectItem value="librarian">Librarian</SelectItem>
                              <SelectItem value="lab_technician">Lab Technician</SelectItem>
                              <SelectItem value="bursar">Bursar</SelectItem>
                              <SelectItem value="head_of_department">Head of Department</SelectItem>
                              <SelectItem value="deputy_principal">Deputy Principal</SelectItem>
                              <SelectItem value="principal">Principal</SelectItem>
                              <SelectItem value="other_staff">Other Staff</SelectItem>
                            </SelectContent>
                          </Select>
                          {formData.position.length === 0 && (
                            <p className="text-[0.8rem] text-muted-foreground">Please select at least one position.</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="emergencyContact" className="text-black dark:text-white">
                          Emergency Contact
                        </Label>
                        <Input
                          id="emergencyContact"
                          value={formData.emergencyContact || ""}
                          onChange={(e) => handleInputChange("emergencyContact", e.target.value)}
                          className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4 border-t border-[#E2E8F0] dark:border-[#1E293B] pt-4">
                    <h3 className="text-lg font-semibold text-black dark:text-white">
                      Contact Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email" className="text-black dark:text-white">
                          Email Address *
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          required
                          className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-black dark:text-white">
                          Phone Number *
                        </Label>
                        <Input
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => handleInputChange("phone", e.target.value)}
                          required
                          className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="secondaryPhone" className="text-black dark:text-white">
                          Secondary Phone Number <span className="text-gray-400 text-xs">(Optional)</span>
                        </Label>
                        <Input
                          id="secondaryPhone"
                          value={formData.secondaryPhone || ""}
                          onChange={(e) => handleInputChange("secondaryPhone", e.target.value)}
                          className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                        />
                      </div>
                      <div></div>
                    </div>
                    <div>
                      <Label htmlFor="physicalAddress" className="text-black dark:text-white">
                        Physical Address
                      </Label>
                      <Textarea
                        id="physicalAddress"
                        value={formData.physicalAddress || ""}
                        onChange={(e) => handleInputChange("physicalAddress", e.target.value)}
                        rows={3}
                        className="rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                      />
                    </div>
                  </div>
                  <div className="space-y-4 border-t border-[#E2E8F0] dark:border-[#1E293B] pt-4">
                    <h3 className="text-lg font-semibold text-black dark:text-white">
                      Professional Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="tscNumber" className="text-black dark:text-white">
                          TSC Number <span className="text-gray-400 text-xs">(Optional)</span>
                        </Label>
                        <Input
                          id="tscNumber"
                          value={formData.tscNumber || ""}
                          onChange={(e) => handleInputChange("tscNumber", e.target.value)}
                          className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                        />
                      </div>
                      <div>
                        <Label htmlFor="kraPin" className="text-black dark:text-white">
                          KRA PIN <span className="text-gray-400 text-xs">(Optional)</span>
                        </Label>
                        <Input
                          id="kraPin"
                          value={formData.kraPin || ""}
                          onChange={(e) => handleInputChange("kraPin", e.target.value)}
                          className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                        />
                      </div>
                    </div>
                    <div className="space-y-4">
                      <Label className="text-base font-medium text-black dark:text-white">
                        Subjects of Expertise
                      </Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4 border rounded-md border-[#E2E8F0] dark:border-[#1E293B] bg-white dark:bg-[#020817]">
                        {subjects.length === 0 ? (
                          <div className="text-sm text-gray-500 dark:text-gray-400 col-span-full">
                            No subjects available
                          </div>
                        ) : (
                          subjects.map(subject => (
                            <div key={subject.subjectId} className="flex items-center space-x-2">
                              <Checkbox
                                id={subject.subjectId}
                                checked={formData.subjectsOfExpertiseId.includes(subject.subjectId)}
                                onCheckedChange={(checked) => handleSubjectChange(subject.subjectId, !!checked)}
                                className="border-[#E2E8F0] mr-1 dark:border-[#1E293B]"
                              />
                              <Label
                                htmlFor={subject.subjectId}
                                className="text-sm font-normal text-black dark:text-white cursor-pointer"
                              >
                                {subject.name}
                              </Label>
                            </div>
                          ))
                        )}
                      </div>
                      {formData.subjectsOfExpertiseId.length > 0 && (
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Selected: {formData.subjectsOfExpertiseId
                            .map(id => subjects.find(s => s.subjectId === id)?.name || id)
                            .join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-4 border-t border-[#E2E8F0] dark:border-[#1E293B] pt-4">
                    <h3 className="text-lg font-semibold text-black dark:text-white">
                      Documents
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="otherDocuments" className="text-black dark:text-white">
                          Other Documents Description <span className="text-gray-400 text-xs">(Optional)</span>
                        </Label>
                        <Textarea
                          id="otherDocuments"
                          value={formData.otherDocuments || ""}
                          onChange={(e) => handleInputChange("otherDocuments", e.target.value)}
                          rows={3}
                          placeholder="Describe any additional documents being uploaded..."
                          className="rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                        />
                      </div>
                      <div>
                        <Label htmlFor="documents" className="text-black dark:text-white">
                          Upload Documents <span className="text-gray-400 text-xs">(Optional)</span>
                        </Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id="documents"
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={handleDocumentsChange}
                            ref={documentsInputRef}
                            className="h-10 rounded-md pl-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white"
                          />
                          <Upload className="h-4 w-4 text-gray-400" />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Accept: PDF, DOC, DOCX, JPG, JPEG, PNG
                        </p>
                        {formData.documents.length > 0 && (
                          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {formData.documents.length} file(s) selected
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-4 pt-6 border-t border-[#E2E8F0] dark:border-[#1E293B]">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onBack}
                      className="m-2 h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31]"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isUploading || isAuthLoading || !hasValidClaims}
                      className="m-2 h-10 bg-[#2563EB] dark:bg-[#3B82F6] text-white hover:bg-[#1E40AF] dark:hover:bg-[#2563EB] flex items-center gap-2"
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Registering...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          Register Teacher/Staff
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
            </ContentLoader>
          </main>

      <Dialog open={isSuccessDialogOpen} onOpenChange={(open) => {
        setIsSuccessDialogOpen(open);
        if (!open) onBack();
      }}>
        <DialogContent className="sm:max-w-[425px] bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-black dark:text-white">Teacher Registered Successfully</DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              {`${formData.firstName} ${formData.lastName} has been added to the system.`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end mt-4">
            <Button
              onClick={() => {
                setIsSuccessDialogOpen(false);
                onBack();
              }}
              className="h-10 bg-[#2563EB] dark:bg-[#3B82F6] text-white hover:bg-[#1E40AF] dark:hover:bg-[#2563EB]"
            >
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isErrorDialogOpen} onOpenChange={setIsErrorDialogOpen}>
        <DialogContent className="sm:max-w-[425px] bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
          <DialogHeader>
            <DialogTitle className="text-black dark:text-white">Registration Failed</DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              {errorMessage}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end mt-4">
            <Button
              onClick={() => setIsErrorDialogOpen(false)}
              className="h-10 bg-[#2563EB] dark:bg-[#3B82F6] text-white hover:bg-[#1E40AF] dark:hover:bg-[#2563EB]"
            >
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>
            </div>
          </div>
        </SidebarProvider>
      </div>
    </SidebarProvider>
  );
};

export default TeacherRegistrationForm;